import React, { useContext } from "react";
import styled, { ThemeContext } from "styled-components";
import PropTypes from "prop-types";
import { Tooltip, IconButton, Button } from "@material-ui/core";

const buttonFontSize = (size) => {
  switch (size) {
    case "extraSmall":
      return 12;
    case "small":
      return 16;
    case "medium":
      return 24;
    case "large":
      return 82;
    default:
      return 32;
  }
};

const StyledIconButton = styled(IconButton)`
  ${({
    buttonsize,
    buttoncolor,
    backgroundcolor,
    margin,
    theme: { colors },
  }) => `
      color: ${colors[buttoncolor]};
      background: ${colors[backgroundcolor]};
      margin: ${
        margin
          ? `${margin[0]}px ${margin[1]}px ${margin[2]}px ${margin[3]}px`
          : 0
      };

      & svg {
        font-size: ${buttonFontSize(buttonsize)}px;
      }
  `}
`;

const StyledButton = styled(Button)`
  ${({ buttoncolor, backgroundcolor, margin, theme: { colors } }) => `
    color: ${colors[buttoncolor]};
    background: ${colors[backgroundcolor]};
    margin: ${
      margin ? `${margin[0]}px ${margin[1]}px ${margin[2]}px ${margin[3]}px` : 0
    };

    &:hover {
      background: ${colors.dark[backgroundcolor]};
    }
  `}
`;

const ButtonWithTooltip = ({
  children,
  tooltip,
  tootltipMessage,
  tooltipPlacement,
  tooltipDisabled,
  iconButton,
  iconColor,
  IconComponent,
  onClick,
  buttonVariant,
  buttonsize,
  buttonDisabled,
  buttoncolor,
  backgroundcolor,
  buttonType,
  margin,
  ...props
}) => {
  const { colors } = useContext(ThemeContext);

  if (tooltip && iconButton) {
    return (
      <Tooltip
        title={tootltipMessage}
        arrow
        placement={tooltipPlacement}
        disableHoverListener={tooltipDisabled}
        disableTouchListener={tooltipDisabled}
        disableFocusListener={tooltipDisabled}
      >
        <span>
          <StyledIconButton
            disabled={buttonDisabled}
            variant={buttonVariant}
            onClick={onClick}
            buttonsize={buttonsize}
            buttoncolor={buttoncolor}
            backgroundcolor={backgroundcolor}
            margin={margin}
          >
            {IconComponent && (
              <IconComponent
                style={!buttonDisabled ? { color: colors[iconColor] } : {}}
              />
            )}
            {children}
          </StyledIconButton>
        </span>
      </Tooltip>
    );
  }

  if (!tooltip && iconButton) {
    return (
      <StyledIconButton
        disabled={buttonDisabled}
        variant={buttonVariant}
        onClick={onClick}
        buttonsize={buttonsize}
        buttoncolor={buttoncolor}
        backgroundcolor={backgroundcolor}
        margin={margin}
      >
        {IconComponent && (
          <IconComponent
            style={!buttonDisabled ? { color: colors[iconColor] } : {}}
          />
        )}
        {children}
      </StyledIconButton>
    );
  }

  if (tooltip && !iconButton) {
    return (
      <Tooltip
        title={tootltipMessage}
        arrow
        placement={tooltipPlacement}
        disableHoverListener={tooltipDisabled}
        disableTouchListener={tooltipDisabled}
        disableFocusListener={tooltipDisabled}
      >
        <StyledButton
          type={buttonType}
          disabled={buttonDisabled}
          variant={buttonVariant}
          onClick={onClick}
          buttonsize={buttonsize}
          buttoncolor={buttoncolor}
          backgroundcolor={backgroundcolor}
          margin={margin}
        >
          {IconComponent && (
            <IconComponent
              style={!buttonDisabled ? { color: colors[iconColor] } : {}}
            />
          )}
          {children}
        </StyledButton>
      </Tooltip>
    );
  }

  return (
    <StyledButton
      disabled={buttonDisabled}
      variant={buttonVariant}
      onClick={onClick}
      buttonsize={buttonsize}
      buttoncolor={buttoncolor}
      backgroundcolor={backgroundcolor}
      type={buttonType}
      margin={margin}
      {...props}
    >
      {IconComponent && (
        <IconComponent
          style={!buttonDisabled ? { color: colors[iconColor] } : {}}
        />
      )}
      {children}
    </StyledButton>
  );
};

ButtonWithTooltip.propTypes = {
  children: PropTypes.any,
  tooltip: PropTypes.bool,
  tootltipMessage: PropTypes.string,
  tooltipPlacement: PropTypes.string,
  tooltipDisabled: PropTypes.bool,
  IconComponent: PropTypes.object,
  iconColor: PropTypes.string,
  iconComponent: PropTypes.bool,
  onClick: PropTypes.func,
  buttoncolor: PropTypes.string,
  backgroundcolor: PropTypes.string,
  buttonVariant: PropTypes.string,
  buttonsize: PropTypes.string,
  buttonDisabled: PropTypes.bool,
  buttonType: PropTypes.string,
  margin: PropTypes.any,
};

ButtonWithTooltip.defaultProps = {
  children: null,
  tooltip: false,
  tootltipMessage: "Tooltip message",
  tooltipPlacement: "top",
  tooltipDisabled: false,
  iconColor: "yellow",
  IconComponent: null,
  iconComponent: false,
  onClick: () => {},
  buttonVariant: "contained",
  buttonsize: "large",
  buttonDisabled: false,
  buttoncolor: "",
  backgroundcolor: "",
  buttonType: "button",
  margin: null,
};

export default ButtonWithTooltip;
